import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Mail, MailX, Activity, ChevronRight } from 'lucide-react';
import { Automations } from '../lib/api';

const KIND_SHORT = {
  manual: 'manual',
  bulk: 'bulk',
  reminder: 'release reminder',
  upcoming: 'recording reminder',
  thank_you: 'thank-you',
  confirmation: 'booking confirmation',
  outreach: 'outreach',
};

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function RecentActivityStrip({ showId, onOpenGuest, onOpenProspect }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await Automations.recentEmails(showId, 6);
        if (mounted) setItems(Array.isArray(r) ? r : []);
      } finally {
        if (mounted) setLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [showId]);

  if (loaded && items.length === 0) return null;  // hide entirely when nothing to show

  return (
    <Card className="border-stone-200 mb-4" data-testid="recent-activity-strip">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-stone-100">
        <Activity className="w-3.5 h-3.5 text-stone-500" />
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Recent activity</div>
      </div>
      <ul className="divide-y divide-stone-100">
        {items.map(it => {
          const failed = it.status === 'failed';
          const openable = it.contactType === 'guest' ? () => onOpenGuest?.(it.guestId) : (it.contactType === 'prospect' ? () => onOpenProspect?.(it.prospectId) : null);
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={openable || undefined}
                disabled={!openable}
                data-testid="recent-activity-item"
                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-stone-50 transition disabled:cursor-default"
              >
                {failed
                  ? <MailX className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                  : <Mail className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                <div className="flex-1 min-w-0 text-[12.5px]">
                  <span className="font-medium text-stone-900">{it.contactName || (it.contactType === 'prospect' ? 'Prospect' : 'Guest')}</span>
                  <span className="text-stone-500"> · {KIND_SHORT[it.kind] || it.kind || 'email'} {failed ? 'failed' : 'sent'}</span>
                  <span className="text-stone-400"> · {relTime(it.sentAt)}</span>
                </div>
                {openable && <ChevronRight className="w-3.5 h-3.5 text-stone-300 shrink-0" />}
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
